<template>
  <div>
    <v-tabs v-model="tab" density="compact">
      <v-tab v-for="tab in visibleTabs" :value="tab">{{ $t(`JobTab${tab}`) }}</v-tab>
    </v-tabs>
    <div class="pt-3">
      <Overview v-if="tab === 'Overview' && isTabAllowed('Overview')"></Overview>
      <Workorders v-if="tab === 'Workorders' && isTabAllowed('Workorders')"></Workorders>
      <Equipment v-if="tab === 'Equipment' && isTabAllowed('Equipment')"></Equipment>
      <Map v-if="tab === 'Map' && isTabAllowed('Map')"></Map>
      <Documents v-if="tab === 'Documents' && isTabAllowed('Documents')"></Documents>
      <Notes v-if="tab === 'Notes' && isTabAllowed('Notes')"></Notes>
      <Printouts v-if="tab === 'Printouts' && isTabAllowed('Printouts')"></Printouts>
      <AdditionalCosts v-if="tab === 'AdditionalCosts' && isTabAllowed('AdditionalCosts')"></AdditionalCosts>
      <CostOverview v-if="tab === 'CostOverview' && isTabAllowed('CostOverview')"></CostOverview>
      <Invoices v-if="tab === 'Invoices' && isTabAllowed('Invoices')"></Invoices>
    </div>
  </div>
</template>

<script setup lang="ts">
import Overview from '@/components/Jobs/View/Overview';
import Workorders from '@/components/Jobs/View/Workorders';
import Equipment from '@/components/Jobs/View/Equipment';
import Map from '@/components/Jobs/View/Map';
import Invoices from '@/components/Jobs/View/Invoices';
import CostOverview from '@/components/Jobs/View/CostOverview';
import Documents from '@/components/Jobs/View/Documents';
import Notes from '@/components/Jobs/View/Notes';
import Printouts from '@/components/Jobs/View/Printouts';
import AdditionalCosts from "@/components/Jobs/View/AdditionalCosts.vue";
import useProjectTabSelector from '@/composable/useProjectTabSelector';

import {computed, provide, ref, watch, onBeforeMount} from 'vue'
import {useRoute} from 'vue-router';
import {useStore} from 'vuex'
import {useI18n} from 'vue-i18n'
import { useAuth } from '@/utils/auth'

const { projectTabSelector } = useProjectTabSelector()

const route = useRoute()
const store = useStore()
const { t } = useI18n()
const auth = useAuth()
const jobId = computed(() => {
  return route.params.id
})
provide('jobId', jobId)

const job: any = computed(() => {
  return store.getters.jobsEdit
})
provide('job', job)

const tabs = ref([
  'Overview',
  'Workorders',
  'Equipment',
  'Map',
  'Documents',
  'Notes',
  'Printouts',
  'AdditionalCosts',
  'CostOverview',
  'Invoices'
])

const tabPermissions: Record<string, string | null> = {
  Overview: 'view jobs',
  Workorders: 'view work orders',
  Equipment: 'view job equipment',
  Map: 'view job equipment',
  Documents: 'view job documents',
  Notes: 'view job notes',
  Printouts: 'view job print-outs',
  AdditionalCosts: 'view job additional costs',
  CostOverview: 'view job cost overview',
  Invoices: 'view job financial documents'
}

const isTabAllowed = (tabName: string) => {
  const permission = tabPermissions[tabName]
  return !permission || auth.check(permission)
}

const visibleTabs = computed(() => {
  return tabs.value.filter((tabName) => isTabAllowed(tabName))
})

const tab = ref(isTabAllowed(projectTabSelector.value) ? projectTabSelector.value : tabs.value.find((tabName) => isTabAllowed(tabName)))

onBeforeMount(async () => {
  await store.dispatch('jobs.fetchSingleForEdit', jobId.value)
  // UI
  store.commit('app.setPageTitle', t('job') + ' ' + job.value.job_name)
  store.commit('app.setBreadcrumbs', [
    { text: t('job', 2), routeName: 'JobsList' },
    { text: t('job') }
  ])
})

watch(tab, (val) => {
  if (val) {
    projectTabSelector.value = tab.value
    // store permanently
    if (tab.value) window.localStorage.setItem('jobs/View/projectTabSelector', tab.value)
  }
}, {immediate: true, deep: true})

</script>

<style lang="scss" scoped>

</style>
